<template>
  <c-box>
    <form novalidate @submit.prevent="submit">
      <c-heading as="h3" size="sm" :my="{ base: 'md', md: '3xl' }">
        {{ $t("productReturn.contactDetails.senderDetails.title") }}
      </c-heading>
      <c-flex
        :gap="{ md: 'xl' }"
        :flex-direction="{ base: 'column', md: 'row' }"
        bg="white"
        :mx="{ base: '-15px', md: '0' }"
        :border-radius="{ md: 'base' }"
        :box-shadow="{ md: 'base' }"
        :px="{ base: '15px', md: 'lg', lg: '3xl', xl: '4xl' }"
        :py="{ base: 'lg', md: 'lg', lg: 'xl', xl: '2.5rem' }"
      >
        <c-box flex="1">
          <!-- partner name -->
          <c-box>
            <c-form-control is-required :is-invalid="inputIsInvalid('partnerName')">
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :mr="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="partnerName">
                  {{ $t("productReturn.contactDetails.partnerDetails.name") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-input
                    disabled
                    id="partnerName"
                    size="lg"
                    flex="1"
                    v-model.trim="form.partnerName"
                    @keyup="onFieldChange('partnerName')"
                    @blur="onFieldBlur('partnerName')"
                  />
                  <c-box
                    v-if="isErrorField('partnerName')"
                    color="brand.red"
                    font-size="sm"
                    my="xs"
                    data-cy="partner-name-error"
                  >
                    {{
                      $t("common.error.fieldRequired", {
                        field: $t("productReturn.contactDetails.partnerDetails.name"),
                      })
                    }}
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </c-box>

          <!-- name -->
          <c-box>
            <c-form-control is-required :is-invalid="inputIsInvalid('fullName')">
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :mr="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="fullName">
                  {{ $t("productReturn.contactDetails.senderDetails.fullName") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-input
                    disabled
                    id="fullName"
                    size="lg"
                    flex="1"
                    v-model.trim="form.fullName"
                    @keyup="onFieldChange('fullName')"
                    @blur="onFieldBlur('fullName')"
                  />
                  <c-box
                    v-if="isErrorField('fullName')"
                    color="brand.red"
                    data-cy="partner-name-error"
                  >
                    {{
                      $t("common.error.fieldRequired", {
                        field: $t("productReturn.contactDetails.senderDetails.fullName"),
                      })
                    }}
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </c-box>
        </c-box>

        <c-box flex="1">
          <!-- email -->
          <c-box>
            <c-form-control is-required :is-invalid="inputIsInvalid('emailAddress')">
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :ml="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="emailAddress">
                  {{ $t("productReturn.contactDetails.senderDetails.emailAddress") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-input
                    disabled
                    id="emailAddress"
                    size="lg"
                    flex="1"
                    v-model.trim="form.emailAddress"
                    @keyup="onFieldChange('emailAddress')"
                    @blur="onFieldBlur('emailAddress')"
                  />
                  <c-box
                    v-if="isErrorField('emailAddress')"
                    color="brand.red"
                    data-cy="partner-name-error"
                  >
                    {{
                      $t("common.error.fieldRequired", {
                        field: $t("productReturn.contactDetails.senderDetails.emailAddress"),
                      })
                    }}
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </c-box>
          <!-- phone number -->
          <c-box>
            <c-form-control is-required>
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                :align-items="{ base: 'baseline', lg: 'inherit' }"
                :ml="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="phoneNumber" :mt="{ lg: 'sm' }">
                  {{ $t("common.user.phoneNumber") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <!-- NOTE: Since this phone number input does not support chakra-ui
                  without some hacky way, so it's better to hardcode the parameterized styles here. -->
                  <VuePhoneNumberInput
                    id="phoneNumber"
                    :border-radius="8"
                    color="#1A1B31"
                    valid-color="#131313"
                    error-color="#E5524C"
                    v-chakra="
                      (theme) => ({
                        '& div.select-country-container': {
                          height: '3rem',
                        },
                        '& input.country-selector__input': {
                          height: '3rem',
                        },
                        '& div.input-tel': {
                          height: '3rem',
                        },
                        '& input.input-tel__input': {
                          height: '3rem',
                        },
                        '& .input-tel__label': {
                          display: 'none',
                        },
                      })
                    "
                    :no-country-selector="false"
                    :show-code-on-list="true"
                    :value="phoneNumberWithoutCountryCallingCode"
                    :default-country-code="defaultPhoneNumberCountryCode"
                    :preferred-countries="['FI', 'SE', 'DE', 'NO', 'DK', 'NL', 'FR', 'ES']"
                    :translations="{
                      countrySelectorLabel: $t('common.user.phoneNumberCountryCode'),
                      countrySelectorError: '',
                      phoneNumberLabel: '',
                      example: '',
                    }"
                    @update="setPhoneNumber"
                    @input="setPhoneNumberInput"
                  />
                  <c-box
                    v-if="isErrorField('phoneNumberInputValue')"
                    color="brand.red"
                    font-size="sm"
                    my="xs"
                    data-cy="phone-number-error"
                  >
                    <span v-if="!getValidationField('phoneNumberInputValue').required">
                      {{
                        $t("common.error.fieldRequired", {
                          field: $t("common.user.phoneNumber"),
                        })
                      }}
                    </span>
                    <span v-if="!getValidationField('phoneNumberInputValue').maxLength">
                      {{
                        $t("common.error.fieldTooLong", {
                          field: $t("common.user.phoneNumber"),
                        })
                      }}
                    </span>
                    <span v-if="!getValidationField('phoneNumberInputValue').minLength">
                      {{
                        $t("common.error.fieldTooShort", {
                          field: $t("common.user.phoneNumber"),
                        })
                      }}
                    </span>
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </c-box>
        </c-box>
      </c-flex>

      <c-flex
        :gap="{ md: 'xl' }"
        :flex-direction="{ base: 'column', md: 'row' }"
        bg="white"
        :mx="{ base: '-15px', md: '0' }"
        :mt="{ base: 'lg', md: '3xl' }"
        :border-radius="{ md: 'base' }"
        :box-shadow="{ md: 'base' }"
        :px="{ base: '15px', md: 'lg', lg: '3xl', xl: '4xl' }"
        :py="{ base: 'lg', md: 'lg', lg: 'xl', xl: '2.5rem' }"
      >
        <c-box flex="1">
          <!-- country -->
          <c-box>
            <c-form-control
              is-required
              :is-invalid="
                inputIsInvalid('partnerCountry') || form.$externalValidationSuccess === false
              "
            >
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :mr="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="partnerCountry">
                  {{ $t("productReturn.contactDetails.partnerDetails.address.country") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-select
                    id="partnerCountry"
                    size="lg"
                    v-model="form.partnerCountry"
                    data-cy="partner-country-selector"
                    @blur="onFieldBlur('partnerCountry')"
                    @change="
                      onFieldBlur('partnerCountry');
                      resetExternalValidationStatus();
                    "
                  >
                    <option v-for="(country, index) in countryList" :key="index" :value="country.v">
                      {{ country.t }}
                    </option>
                  </c-select>

                  <c-box
                    v-if="isErrorField('partnerCountry')"
                    color="brand.red"
                    font-size="sm"
                    my="xs"
                    data-cy="country-error"
                  >
                    {{ $t("productReturn.contactDetails.partnerDetails.address.country") }}
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </c-box>

          <!-- street address -->
          <c-box>
            <c-form-control is-required :is-invalid="inputIsInvalid('partnerAddress1')">
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :mr="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="partnerAddress1">
                  {{ $t("productReturn.contactDetails.partnerDetails.address.address1") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-input
                    id="partnerAddress1"
                    size="lg"
                    flex="1"
                    v-model.trim="form.partnerAddress1"
                    @keyup="onFieldChange('partnerAddress1')"
                    @blur="onFieldBlur('partnerAddress1')"
                  />
                  <c-box
                    v-if="isErrorField('partnerAddress1')"
                    color="brand.red"
                    font-size="sm"
                    my="xs"
                    data-cy="street-address-error"
                  >
                    <c-text as="span" v-if="!getValidationField('partnerAddress1').required">
                      {{
                        $t("common.error.fieldRequired", {
                          field: $t("productReturn.contactDetails.partnerDetails.address.address1"),
                        })
                      }}
                    </c-text>
                    <c-text as="span" v-else-if="!getValidationField('partnerAddress1').maxLength">
                      {{
                        $t("common.error.fieldTooLong", {
                          field: $t("productReturn.contactDetails.partnerDetails.address.address1"),
                        })
                      }}
                    </c-text>
                    <c-text as="span" v-else-if="!getValidationField('partnerAddress1').invalid">
                      {{
                        $t("common.error.fieldInvalid", {
                          field: $t("productReturn.contactDetails.partnerDetails.address.address1"),
                        })
                      }}
                    </c-text>
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </c-box>

          <!-- street address 2 -->
          <c-box>
            <c-form-control :is-invalid="inputIsInvalid('partnerAddress2')">
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :mr="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="partnerAddress2">
                  {{ `${$t("common.address.streetAddress")} 2` }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-input
                    id="partnerAddress2"
                    size="lg"
                    flex="1"
                    v-model.trim="form.partnerAddress2"
                    @keyup="onFieldChange('partnerAddress2')"
                    @blur="onFieldBlur('partnerAddress2')"
                  />
                  <c-box
                    v-if="isErrorField('partnerAddress2')"
                    color="brand.red"
                    font-size="sm"
                    my="xs"
                    data-cy="street-address-2-error"
                  >
                    <c-box v-if="!getValidationField('partnerAddress2').maxLength">
                      {{
                        $t("common.error.fieldTooLong", {
                          field: $t("productReturn.contactDetails.partnerDetails.address.address2"),
                        })
                      }}
                    </c-box>
                    <c-box v-else-if="!getValidationField('partnerAddress2').invalid">
                      {{
                        $t("common.error.fieldInvalid", {
                          field: $t("productReturn.contactDetails.partnerDetails.address.address2"),
                        })
                      }}
                    </c-box>
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </c-box>
        </c-box>

        <c-box flex="1">
          <!-- postal code -->
          <c-box>
            <c-form-control
              is-required
              :is-invalid="
                inputIsInvalid('partnerZipCode') || form.$externalValidationSuccess === false
              "
            >
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :ml="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="partnerZipCode">
                  {{ $t("productReturn.contactDetails.partnerDetails.address.zipCode") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-input
                    id="partnerZipCode"
                    :disabled="countriesWithoutPostalCodesList.includes(form.partnerCountry)"
                    size="lg"
                    flex="1"
                    v-model.trim="form.partnerZipCode"
                    @keyup="
                      onFieldChange('partnerZipCode');
                      resetExternalValidationStatus();
                    "
                    @blur="onFieldBlur('partnerZipCode')"
                    data-cy="partner-zip-code-input"
                  />
                  <c-box
                    v-if="isErrorField('partnerZipCode')"
                    color="brand.red"
                    font-size="sm"
                    my="xs"
                    data-cy="zip-code-error"
                  >
                    <c-text as="span" v-if="!getValidationField('partnerZipCode').required">
                      {{
                        $t("common.error.fieldRequired", {
                          field: $t("productReturn.contactDetails.partnerDetails.address.zipCode"),
                        })
                      }}
                    </c-text>
                    <c-text as="span" v-else-if="!getValidationField('partnerZipCode').maxLength">
                      {{
                        $t("common.error.fieldTooLong", {
                          field: $t("productReturn.contactDetails.partnerDetails.address.zipCode"),
                        })
                      }}
                    </c-text>
                    <c-text as="span" v-else-if="!getValidationField('partnerZipCode').invalid">
                      {{
                        $t("common.error.fieldInvalid", {
                          field: $t("productReturn.contactDetails.partnerDetails.address.zipCode"),
                        })
                      }}
                    </c-text>
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </c-box>

          <!-- city -->
          <c-form-control :is-invalid="inputIsInvalid('partnerCity')" is-required>
            <c-flex
              d="flex"
              :ml="{ xl: 'md' }"
              mb="lg"
              min-height="46px"
              :align-items="cityNotSelectedFromListMessage ? 'baseline' : 'center'"
              overflow="visible"
              font-size="md"
            >
              <c-form-label
                :min-width="{ xl: '12rem' }"
                :mr="{ base: 'lg', xl: '0' }"
                font-weight="400"
              >
                {{ $t("common.address.city") }}
              </c-form-label>
              <Spinner v-if="cityLoading" size="md" />
              <c-box v-else :text-align="{ base: 'right', lg: 'left' }" flex="1" width="100%">
                <c-box v-if="countriesWithoutPostalCodesList.includes(form.partnerCountry)">
                  <QuickCitySearch
                    width="100%"
                    :countryCode="form.partnerCountry"
                    :isPickup="false"
                    :isDHL="true"
                    :zipCode="form.partnerZipCode"
                    :city="form.partnerCity"
                    @set-delivery-city="setReturnCity"
                  >
                  </QuickCitySearch>
                </c-box>
                <c-text
                  v-else-if="
                    (isErrorField('partnerCity') && !cityNotSelectedFromListMessage) ||
                    form.$externalValidationSuccess === false
                  "
                  as="span"
                  color="brand.red"
                  data-cy="city-error"
                >
                  <c-text as="span" v-if="cityErrorMessage" data-cy="address-city-error">
                    {{ $t(cityErrorMessage) }}
                  </c-text>
                  <c-text
                    v-else-if="missingFieldsForGettingCity"
                    as="span"
                    font-style="italic"
                    color="gray.600"
                  >
                    {{ $t("common.address.city.empty") }}
                  </c-text>
                  <c-text as="span" v-else color="gray.600" data-cy="address-city-error">
                    {{ $t("common.address.city.unknownError") }}
                  </c-text>
                </c-text>
                <c-select
                  v-else-if="form.cities.length === 1"
                  id="city"
                  size="lg"
                  v-model="form.partnerCity"
                  data-cy="city-value"
                >
                  <option :value="form.partnerCity" selected>
                    {{ form.partnerCity }}
                  </option>
                </c-select>
                <c-select
                  v-else-if="form.cities.length > 1"
                  id="city"
                  size="lg"
                  v-model="form.partnerCity"
                  @blur="onFieldBlur('partnerCity')"
                  @change="onFieldBlur('partnerCity')"
                  data-cy="multiple-city-values"
                >
                  <option
                    v-for="(city, index) in form.cities"
                    :key="index"
                    :value="city"
                    :selected="city === form.partnerCity"
                  >
                    {{ city }}
                  </option>
                </c-select>

                <c-text v-else as="span" font-style="italic" color="gray.600">
                  {{ $t("common.address.city.empty") }}
                </c-text>

                <c-text
                  v-if="
                    cityNotSelectedFromListMessage &&
                    !countriesWithoutPostalCodesList.includes(form.partnerCountry)
                  "
                  color="brand.red"
                  data-cy="city-error"
                  mt="xs"
                >
                  {{ $t(cityNotSelectedFromListMessage) }}
                </c-text>
              </c-box>
            </c-flex>
          </c-form-control>
          <!-- province code -->
          <c-box v-if="countrySubdivisionList.length">
            <c-form-control is-required :is-invalid="inputIsInvalid('partnerStateProvinceCode')">
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :ml="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="partnerStateProvinceCode">
                  {{
                    $t(
                      `productReturn.contactDetails.partnerDetails.address.countrySubdivision.${form.partnerCountry}`,
                    )
                  }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-select
                    id="partnerStateProvinceCode"
                    size="lg"
                    v-model="form.partnerStateProvinceCode"
                    data-cy="province-code-selector"
                    @blur="onFieldBlur('partnerStateProvinceCode')"
                    @change="onFieldBlur('partnerStateProvinceCode')"
                  >
                    <option
                      v-for="(countrySubdivision, index) in countrySubdivisionList"
                      :key="index"
                      :value="countrySubdivision.v"
                    >
                      {{ countrySubdivision.t }}
                    </option>
                  </c-select>

                  <c-box
                    v-if="isErrorField('partnerStateProvinceCode')"
                    color="brand.red"
                    font-size="sm"
                    my="xs"
                    data-cy="province-code-error"
                  >
                    {{
                      $t("common.error.fieldRequired", {
                        field: $t(
                          `productReturn.contactDetails.partnerDetails.address.countrySubdivision.${form.partnerCountry}`,
                        ),
                      })
                    }}
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </c-box>
        </c-box>
      </c-flex>
    </form>
  </c-box>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { required, email, minLength, maxLength, numeric, helpers } from "vuelidate/lib/validators";
import { FormValidationMixin } from "@/components/FormValidationMixin.js";
import {
  ReturnType,
  rules,
  countryList,
  countrySubdivisionList,
  ICountrySubdivision,
  ResponseStatus,
  ShipperType,
  countriesWithoutPostalCodesList,
} from "@iloq/shared";
import { getPhoneNumberInfo } from "@/util/PhoneNumber";
import { mapState } from "vuex";
import VuePhoneNumberInput from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";
import Heading from "../../design-system/elements/Heading.vue";
import {
  CBox,
  CFlex,
  CFormControl,
  CFormLabel,
  CHeading,
  CInput,
  CSelect,
  CText,
} from "@chakra-ui/vue";
import { inputMixin } from "./RMAMixins";
import { debounce, DebouncedFunc } from "lodash";
import { Logger } from "@iloq/shared";
import { AxiosError } from "axios";
import AddressValidationApi from "../../api/AddressValidationApi";
import Spinner from "@/components/Spinner.vue";
import QuickCitySearch from "@/components/products/QuickCitySearch.vue";
import { IRootState } from "@iloq/shared";

// @ts-expect-error Helpers does contain regex
const alphanumericSpacesAndSpecialChars = helpers.regex(
  "alpha",
  rules.regexp.alphaNumericWithSpecialChars,
);
const stateExists = (value: string, siblings: any) =>
  !["US", "CA", "IE"].includes(siblings.partnerCountry) || (value || "").trim().length >= 2;

const validateCommonAddress = {
  form: {
    fullName: { required },
    emailAddress: { required, email },
    phoneNumberInputValue: {
      required,
      maxLength: maxLength(rules.maxLength.global.phoneNumber),
    },
    partnerName: { required },
    partnerAddress1: {
      required,
      alphanumericSpacesAndSpecialChars,
      maxLength: maxLength(rules.maxLength.global.streetAddress),
    },
    partnerAddress2: {
      alphanumericSpacesAndSpecialChars,
      maxLength: maxLength(rules.maxLength.global.streetAddress2),
    },
    partnerZipCode: { required, maxLength: maxLength(16) },
    partnerCity: { required },
    partnerStateProvinceCode: { stateExists },
    partnerCountry: { required, maxLength: maxLength(2) },
  },
};

const validateFIAddress = {
  form: {
    fullName: { required },
    emailAddress: { required, email },
    phoneNumberInputValue: {
      required,
      maxLength: maxLength(rules.maxLength.global.phoneNumber),
    },
    partnerName: { required },
    partnerAddress1: {
      required,
      alphanumericSpacesAndSpecialChars,
      maxLength: maxLength(rules.maxLength.finnish.streetAddress),
    },
    partnerAddress2: {
      alphanumericSpacesAndSpecialChars,
      maxLength: maxLength(rules.maxLength.finnish.streetAddress2),
    },
    partnerZipCode: {
      required,
      numeric,
      maxLength: maxLength(rules.maxLength.finnish.zipCode),
      minLength: minLength(rules.maxLength.finnish.zipCode),
    },
    partnerCity: {
      required,
      maxLength: maxLength(rules.maxLength.global.city),
    },
    partnerCountry: { required, maxLength: maxLength(2) },
  },
};

const validateUSAddress = {
  form: {
    fullName: { required },
    emailAddress: { required, email },
    phoneNumberInputValue: {
      required,
      maxLength: maxLength(rules.maxLength.global.phoneNumber),
      minLength: minLength(rules.minLength.us.phoneNumber),
    },
    partnerName: { required },
    partnerAddress1: {
      required,
      alphanumericSpacesAndSpecialChars,
      maxLength: maxLength(rules.maxLength.global.streetAddress),
    },
    partnerAddress2: {
      alphanumericSpacesAndSpecialChars,
      maxLength: maxLength(rules.maxLength.global.streetAddress2),
    },
    partnerZipCode: { required, maxLength: maxLength(rules.maxLength.global.zipCode) },
    partnerStateProvinceCode: { stateExists },
    partnerCity: {
      required,
      maxLength: maxLength(rules.maxLength.global.city),
    },
    partnerCountry: { required, maxLength: maxLength(2) },
  },
};

export default defineComponent({
  name: "RMA01SenderDetails",
  mixins: [inputMixin, FormValidationMixin],
  components: {
    VuePhoneNumberInput,
    Heading,
    CBox,
    CSelect,
    CInput,
    CHeading,
    CText,
    CFlex,
    CFormControl,
    CFormLabel,
    Spinner,
    QuickCitySearch,
  },
  validations() {
    const country = this.form.partnerCountry;
    switch (country) {
      case "US":
        return validateUSAddress;
      case "FI":
        return validateFIAddress;
      default:
        return validateCommonAddress;
    }
  },
  computed: {
    ...mapState("partnerInfo", {
      partnerInfo: (state: unknown) => ({
        ...(state as IRootState).partnerInfo,
        phoneNumberInputValue: "",
      }),
    }),
    ...mapState({
      user: (state: unknown) => (state as IRootState).user,
    }),
    shouldBeValidatedWithDHLEndpoint(): boolean {
      return this.$store?.state?.features?.useDHLInAddressValidation;
    },
    cityErrorMessage(): string | undefined {
      return ((this.errors as any[]) || []).find(
        (err: any) => err.field === "partnerCity" && err.message !== null,
      )?.message as string | undefined;
    },
    cityNotSelectedFromListMessage(): string | undefined {
      return ((this.errors as any[]) || []).find(
        (err: any) =>
          err.field === "partnerCity" &&
          err.message === "common.address.city.notSelectedFromListOfCities",
      )?.message as string | undefined;
    },
    missingFieldsForGettingCity(): boolean {
      return !this.form.partnerZipCode || !this.form.partnerCountry;
    },
    phoneNumberWithoutCountryCallingCode(): string {
      if (this.form.phoneNumberInputValue) {
        return this.form.phoneNumberInputValue;
      }

      if (this.form.phoneNumberInputValue === null) {
        return "";
      }

      if (!this.form.phoneNumber) {
        return "";
      }

      return getPhoneNumberInfo(this.form.phoneNumber, this.form.partnerCountry)
        .phoneNumberWithoutCountryCallingCode;
    },
    defaultPhoneNumberCountryCode(): string {
      return getPhoneNumberInfo(this.form.phoneNumber, this.form.partnerCountry).countryCode;
    },
  },
  data() {
    return {
      cityLoading: false,
      form: {
        fullName: "",
        emailAddress: "",
        phoneNumber: "",
        partnerAddress1: "",
        partnerAddress2: "",
        partnerZipCode: "",
        partnerStateProvinceCode: undefined,
        partnerCity: "",
        partnerCountry: "FI",
        phoneNumberInputValue: "",
        $externalValidationSuccess: undefined,
        cities: [] as string[],
      },
      countryList: countryList(),
      countrySubdivisionList: [] as ICountrySubdivision[],
      countriesWithoutPostalCodesList: countriesWithoutPostalCodesList(),
      debouncedAddressChange: null as DebouncedFunc<any> | null,
    };
  },
  props: {
    returnType: {
      type: String as PropType<ReturnType>,
      required: true,
    },
    addressValidationApi: {
      type: Object as PropType<AddressValidationApi>,
      required: true,
    },
    shipper: {
      type: String as PropType<ShipperType>,
      required: true,
    },
  },
  beforeMount() {
    if (
      this.form.partnerCountry &&
      this.form.partnerZipCode &&
      !this.form.cities?.length &&
      !this.countriesWithoutPostalCodesList.includes(this.form.partnerCountry)
    ) {
      this.handleCountryCodeAndZipCodeValidationAndCityUpdate();
    }
  },
  methods: {
    async handleCountryCodeAndZipCodeValidationAndCityUpdate(): Promise<void> {
      this.cityLoading = true;
      try {
        await this.handleCountryAndZipCodeValidationAndCityUpdate();
      } catch {
        // Ignore error
      }
      this.cityLoading = false;
    },
    async handleCountryAndZipCodeValidationAndCityUpdate(): Promise<void> {
      if (this.countriesWithoutPostalCodesList.includes(this.form.partnerCountry)) {
        this.removeFormError("partnerZipCode");
        this.removeFormError("partnerCity");
        this.form.partnerZipCode = "00000";
        this.form.partnerCity = "";
        return;
      } else if (this.missingFieldsForGettingCity) {
        return this.updateFormError("partnerCity", null);
      }

      try {
        const cities = await this.addressValidationApi.validateAddressAndGetCities(
          this.form.partnerZipCode,
          this.form.partnerCountry,
          this.shouldBeValidatedWithDHLEndpoint,
          false,
          this.form.partnerCity,
        );

        this.form.cities = cities;

        if (cities.length === 1) {
          const previousCity = this.form.partnerCity;
          this.form.partnerCity = cities[0];
          this.removeFormError("partnerCity");
          if (previousCity === this.form.partnerCity) {
            this.$emit("addressSettled");
          }
        } else {
          if (cities.includes(this.form.partnerCity)) {
            this.removeFormError("partnerCity");
            this.$emit("addressSettled");
          } else {
            this.form.partnerCity = "";
            this.updateFormError("partnerCity", "common.address.city.notSelectedFromListOfCities");
          }
        }
      } catch (error) {
        const err = error as AxiosError;
        this.form.partnerCity = "";

        if (err.response?.status === ResponseStatus.notFound) {
          Logger.debug(err);
          this.updateFormError("partnerCity", "common.address.city.notFound");
        } else {
          Logger.error(err);
          this.updateFormError("partnerCity", "common.address.city.unknownError");
        }
        throw err;
      }
    },
    updateFormError(field: string, message: string | null): void {
      this.removeFormError(field);
      this.addFormError(field, message);
    },
    resetExternalValidationStatus(): void {
      this.form.$externalValidationSuccess = undefined;
    },
    makeUpperCase(t: any): string | undefined {
      return t?.toUpperCase();
    },
    setPhoneNumber(payload: any): void {
      if (payload.formatInternational) {
        this.form.phoneNumber = payload.formatInternational;
      }
    },
    setPhoneNumberInput(phoneNumber: string): void {
      this.form.phoneNumberInputValue = phoneNumber;
      (this as any).$v.form.phoneNumberInputValue.$touch();
      (this as any).onFieldChange("phoneNumberInputValue");
    },
    setReturnCity(city: string): void {
      this.form.partnerCity = city;
    },
  },
  created() {
    this.debouncedAddressChange = debounce(
      this.handleCountryCodeAndZipCodeValidationAndCityUpdate,
      500,
    );
  },
  watch: {
    "form.partnerZipCode": function () {
      this.debouncedAddressChange();
    },
    "form.partnerCountry": function () {
      this.$set(this.form, "partnerStateProvinceCode", undefined);
      if (this.form.partnerCountry) {
        this.countrySubdivisionList = countrySubdivisionList(this.form.partnerCountry) || [];
        if (this.countrySubdivisionList.length > 0) {
          this.$set(this.form, "partnerStateProvinceCode", "");
        }
      } else {
        this.countrySubdivisionList = [];
      }
      this.debouncedAddressChange();
    },
    "form.partnerCity": function (newCity: string) {
      if (
        this.form.partnerCountry &&
        this.form.partnerZipCode &&
        this.form.partnerCity === "" &&
        !this.countriesWithoutPostalCodesList.includes(this.form.partnerCountry)
      ) {
        this.debouncedAddressChange();
      }
      if (newCity) {
        this.$emit("addressSettled");
      }
    },
  },
});
</script>
